import React, { useState } from 'react';
import {
  MdContentCopy,
  MdDelete,
  MdEdit,
  MdInfoOutline,
  MdMoreVert,
  MdSend,
} from 'react-icons/md';
import {
  ChipMenu,
  IconButton,
  CHIP_MENU_ALIGN_RIGHT,
  CHIP_MENU_ITEM_TONE_DANGER,
  type IconButtonSize,
} from '@aibuild/ui';
import {
  ActionsAnchor,
  ActionsMenuPosition,
} from './ProcessActionsMenu.styled';

export interface ProcessActionsMenuProps {
  /**
   * Accessible name for the kebab trigger and the resulting menu — usually
   * something like `Actions for "Untitled process"`. Required because the
   * trigger is icon-only.
   */
  ariaLabel: string;
  /**
   * Trigger icon size. Matches `IconButton`'s sizes — `sm` for tight rows,
   * `md` (default) for headers, `lg` for top-level toolbars.
   */
  triggerSize?: IconButtonSize;
  /**
   * Disable the whole control (e.g. while a global mutation is in flight).
   * The menu auto-closes if it was open.
   */
  disabled?: boolean;
  /** Optional `Details` action — hidden when not provided. */
  onDetails?: () => void;
  /**
   * Optional `Rename` action — hidden when not provided. List rows hide
   * this for non-owners / published rows; the editor's panel kebab passes
   * it whenever the topbar's inline-rename is enabled.
   */
  onRename?: () => void;
  /** Optional `Duplicate` action — hidden when not provided. */
  onDuplicate?: () => void;
  /** Optional `Send copy…` action — hidden when not provided. */
  onSendCopy?: () => void;
  /** Optional `Delete` action — hidden when not provided. */
  onDelete?: () => void;
  /**
   * When true, the Delete row renders disabled with `deleteTooltip`. Use
   * this for the "row is published / not owner" cases where we want to
   * surface the affordance and explain why it can't fire, rather than
   * silently hiding it.
   */
  deleteDisabled?: boolean;
  deleteTooltip?: string;
  dataTestId?: string;
}

/**
 * Presentational kebab menu used by both the Processes list (per-row
 * actions) and the Process editor's side-panel header. Owns the open/
 * close state and the kebab trigger; the parent wires the actions.
 *
 * Why a separate component (and not just a `ChipMenu` inline)? Because
 * the EXACT same five actions appear in two contexts with the same
 * gating story (Rename hides when the process is read-only; Delete
 * disables with a tooltip when not allowed). Putting the menu shape
 * in one place means future actions (`Move to folder`, `Add tag`, …)
 * land in both contexts at once and keeps the menu visuals locked
 * to the design across surfaces.
 */
const ProcessActionsMenu: React.FC<ProcessActionsMenuProps> = ({
  ariaLabel,
  triggerSize = 'md',
  disabled = false,
  onDetails,
  onRename,
  onDuplicate,
  onSendCopy,
  onDelete,
  deleteDisabled = false,
  deleteTooltip,
  dataTestId = 'process-actions-menu',
}) => {
  // `menuOpen` tracks user intent (clicked the kebab); `isOpen` is the
  // effective render-time state, gated by `disabled` so the menu self-
  // hides the instant a mutation starts elsewhere. Splitting the names
  // satisfies `react/hook-use-state` (symmetric [x, setX]) AND avoids
  // the `react-hooks/set-state-in-effect` anti-pattern of mirroring
  // props into state via `useEffect`.
  const [menuOpen, setMenuOpen] = useState(false);
  const isOpen = menuOpen && !disabled;

  const close = () => setMenuOpen(false);

  const handleTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (disabled) return;
    setMenuOpen((prev) => !prev);
  };

  const wrap = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    close();
    action();
  };

  return (
    <ActionsAnchor
      data-testid={dataTestId}
      onClick={(e) => e.stopPropagation()}
    >
      <IconButton
        ariaLabel={ariaLabel}
        icon={<MdMoreVert size={18} />}
        size={triggerSize}
        variant="ghost"
        onClick={handleTriggerClick}
        disabled={disabled}
        dataTestId={`${dataTestId}__trigger`}
      />
      {isOpen && (
        <ActionsMenuPosition>
          <ChipMenu
            onClose={close}
            align={CHIP_MENU_ALIGN_RIGHT}
            ariaLabel={ariaLabel}
            dataTestId={`${dataTestId}__menu`}
          >
            <ChipMenu.List>
              {onDetails && (
                <ChipMenu.Item
                  icon={<MdInfoOutline size={16} />}
                  onClick={wrap(onDetails)}
                  dataTestId={`${dataTestId}__details`}
                >
                  Details
                </ChipMenu.Item>
              )}
              {onRename && (
                <ChipMenu.Item
                  icon={<MdEdit size={16} />}
                  onClick={wrap(onRename)}
                  dataTestId={`${dataTestId}__rename`}
                >
                  Rename
                </ChipMenu.Item>
              )}
              {onDuplicate && (
                <ChipMenu.Item
                  icon={<MdContentCopy size={16} />}
                  onClick={wrap(onDuplicate)}
                  dataTestId={`${dataTestId}__duplicate`}
                >
                  Duplicate
                </ChipMenu.Item>
              )}
              {onSendCopy && (
                <ChipMenu.Item
                  icon={<MdSend size={16} />}
                  onClick={wrap(onSendCopy)}
                  dataTestId={`${dataTestId}__send-copy`}
                >
                  Send copy…
                </ChipMenu.Item>
              )}
              {onDelete && (
                <>
                  <ChipMenu.Separator />
                  <ChipMenu.Item
                    icon={<MdDelete size={16} />}
                    tone={CHIP_MENU_ITEM_TONE_DANGER}
                    disabled={deleteDisabled}
                    title={deleteTooltip}
                    onClick={deleteDisabled ? undefined : wrap(onDelete)}
                    dataTestId={`${dataTestId}__delete`}
                  >
                    Delete
                  </ChipMenu.Item>
                </>
              )}
            </ChipMenu.List>
          </ChipMenu>
        </ActionsMenuPosition>
      )}
    </ActionsAnchor>
  );
};

export default ProcessActionsMenu;
